import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCurrentUser } from '@/hooks/useAuth';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import type { AssistantPermissions } from '@/types/entities';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import {
  useOrders,
  useConfirmOrderCancellation,
  useRejectOrderCancellation,
} from '@/hooks/useOrders';
import { toast } from '@/hooks/use-toast';
import { Header } from '@/components/layout/Header';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { CheckCircle, X, AlertCircle, Calendar, TrendingDown } from 'lucide-react';
import type { Order } from '@/types/entities';

function AdminCancellationDashboardContent() {
  const { t, i18n } = useTranslation();
  const { data: user } = useCurrentUser();
  const { data: assistant } = useCurrentAssistant();
  const { selectedRestaurantId } = useRestaurantContext();
  const { data: allOrders = [] } = useOrders(selectedRestaurantId);
  const confirmCancellation = useConfirmOrderCancellation(selectedRestaurantId || '');
  const rejectCancellation = useRejectOrderCancellation(selectedRestaurantId || '');

  // Permission helper function
  const hasPermission = (page: keyof AssistantPermissions, action: string): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return pagePermissions[action] === true;
  };

  // Check if user can accept/reject cancellations
  // If assistant is logged in, check their accept_reject permission
  // If not an assistant (admin user), always allow
  const canAcceptReject = assistant
    ? hasPermission('cancellations', 'accept_reject')
    : true;

  // Listen for cancellation request notifications
  useEffect(() => {
    const handleCancellationRequested = (event: CustomEvent) => {
      const { orderId } = event.detail;

      // Find the order to get table number - check both parent orders and sub-orders
      let order: Order | undefined;
      let tableNumber: number | string = '?';

      // First, try to find it as a parent order
      order = allOrders.find((o) => o.id === orderId);

      // If not found, check sub-orders
      if (!order) {
        for (const parentOrder of allOrders) {
          const subOrder = parentOrder.subOrders?.find((so) => so.id === orderId);
          if (subOrder) {
            order = subOrder;
            break;
          }
        }
      }

      // Get table number from the order (sub-orders inherit table from parent)
      if (order) {
        // For sub-orders, get table from parent order
        if (order.parentOrderId) {
          const parentOrder = allOrders.find((o) => o.subOrders?.some((so) => so.id === orderId));
          tableNumber = parentOrder?.table?.tableNumber || '?';
        } else {
          tableNumber = order.table?.tableNumber || '?';
        }
      }

      // Play sound notification
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {
          console.log('Audio notification failed');
        });
      } catch (error) {
        console.error('Failed to play notification sound:', error);
      }

      // Show toast notification
      toast({
        title: t('order.cancellationRequested'),
        description: t('order.cancellationRequestedForTable', { number: tableNumber }),
        duration: 5000,
      });
    };

    window.addEventListener('cancellation-requested', handleCancellationRequested as EventListener);
    return () => {
      window.removeEventListener(
        'cancellation-requested',
        handleCancellationRequested as EventListener
      );
    };
  }, [t, allOrders]);

  // Get all orders with pending cancellation requests
  const pendingCancellationOrders = useMemo(() => {
    const ordersWithCancellation: Order[] = [];

    allOrders.forEach((order) => {
      // Check parent orders - pending if requested but not cancelled
      if (!order.parentOrderId && order.cancellationRequestedBy && order.status !== 'cancelled') {
        ordersWithCancellation.push(order);
      }

      // Check sub-orders
      if (order.subOrders) {
        order.subOrders.forEach((subOrder) => {
          if (subOrder.cancellationRequestedBy && subOrder.status !== 'cancelled') {
            ordersWithCancellation.push(subOrder);
          }
        });
      }
    });

    return ordersWithCancellation;
  }, [allOrders]);

  // Get all cancelled orders (confirmed cancellations)
  // Only count orders that were cancelled through a cancellation request (not manually cancelled)
  // Avoid double-counting: if a sub-order's parent is also cancelled, only show the parent
  const cancelledOrders = useMemo(() => {
    const cancelled: Order[] = [];

    allOrders.forEach((order) => {
      // Check parent orders - must have been requested AND cancelled
      if (
        !order.parentOrderId &&
        order.cancellationRequestedBy &&
        order.status === 'cancelled'
      ) {
        cancelled.push(order);
      }

      // Check sub-orders - must have been requested AND cancelled
      // BUT only include if parent is NOT cancelled (to avoid double counting)
      if (order.subOrders) {
        const isParentCancelled = order.status === 'cancelled';

        order.subOrders.forEach((subOrder) => {
          if (
            subOrder.cancellationRequestedBy &&
            subOrder.status === 'cancelled' &&
            !isParentCancelled // Only show sub-order if parent is NOT cancelled
          ) {
            cancelled.push(subOrder);
          }
        });
      }
    });

    return cancelled.sort((a, b) => {
      const dateA = a.cancellationRequestedAt
        ? new Date(a.cancellationRequestedAt).getTime()
        : new Date(a.updatedAt).getTime();
      const dateB = b.cancellationRequestedAt
        ? new Date(b.cancellationRequestedAt).getTime()
        : new Date(b.updatedAt).getTime();
      return dateB - dateA; // Most recent first
    });
  }, [allOrders]);

  // Calculate cancellation statistics
  const cancellationStats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    let todayCount = 0;
    let weekCount = 0;
    let monthCount = 0;

    cancelledOrders.forEach((order) => {
      const cancelledDate = order.cancellationRequestedAt
        ? new Date(order.cancellationRequestedAt)
        : new Date(order.updatedAt);

      if (cancelledDate >= startOfToday) {
        todayCount++;
      }
      if (cancelledDate >= startOfWeek) {
        weekCount++;
      }
      if (cancelledDate >= startOfMonth) {
        monthCount++;
      }
    });

    return { todayCount, weekCount, monthCount };
  }, [cancelledOrders]);

  // Get last 5 cancellations
  const last5Cancellations = useMemo(() => {
    return cancelledOrders.slice(0, 5);
  }, [cancelledOrders]);

  const handleConfirmCancellation = async (orderId: string) => {
    // Get actor ID - either from admin user or assistant
    // The hooks handle permission verification server-side for assistants
    const actorId = user?.id || assistant?.id;
    if (!actorId) return;

    try {
      await confirmCancellation.mutateAsync({
        orderId,
        staffId: actorId,
      });
      toast({
        title: t('common.success'),
        description: t('order.cancellationConfirmed'),
      });
    } catch (error: any) {
      console.error('Failed to confirm cancellation:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  const handleRejectCancellation = async (orderId: string) => {
    // Get actor ID - either from admin user or assistant
    // The hooks handle permission verification server-side for assistants
    const actorId = user?.id || assistant?.id;
    if (!actorId) return;

    try {
      await rejectCancellation.mutateAsync({
        orderId,
        staffId: actorId,
      });
      toast({
        title: t('common.success'),
        description: t('order.cancellationRejected'),
      });
    } catch (error: any) {
      console.error('Failed to reject cancellation:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  // Authorization is handled by ProtectedRoute wrapper
  // This check is removed to allow assistants with proper permissions

  return (
    <ProtectedRoute requiredRole="admin" requiredPermission="cancellations">
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">
              {canAcceptReject
                ? t('order.pendingCancellationRequests')
                : t('order.cancellationStatistics')}
            </h1>
            <p className="text-muted-foreground">
              {canAcceptReject
                ? t('order.reviewCancellationRequests')
                : t('order.viewCancellationStatistics')}
            </p>
          </div>

          {/* Pending Cancellation Requests - Only shown if user can accept/reject */}
          {canAcceptReject && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  {t('order.pendingCancellationRequests')} ({pendingCancellationOrders.length})
                </CardTitle>
                <CardDescription>{t('order.reviewCancellationRequests')}</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingCancellationOrders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('order.noPendingCancellations')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingCancellationOrders.map((order) => {
                      const isSubOrder = !!order.parentOrderId;
                      const tableNumber = order.table?.tableNumber || '?';

                      // For parent orders, get items from all sub-orders
                      // For sub-orders, use the order's own items
                      const orderItems = isSubOrder
                        ? order.items || []
                        : order.subOrders?.flatMap((subOrder) => subOrder.items || []) || [];

                      const orderTotal = orderItems.reduce((sum, item) => {
                        const price = item.priceAtOrder || item.menuItem?.price || 0;
                        return sum + price * item.quantity;
                      }, 0);

                      return (
                        <Card key={order.id} className="border-orange-200 bg-orange-50/50">
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <Badge className="bg-orange-500">
                                    {isSubOrder ? t('order.subOrder') : t('order.order')}
                                  </Badge>
                                  <span className="font-mono text-xs sm:text-sm text-muted-foreground">
                                    #{order.id.slice(0, 8)}
                                  </span>
                                </div>
                                <p className="text-sm font-medium">
                                  {t('tables.tableNumber', { number: tableNumber })}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(order.createdAt).toLocaleString()}
                                </p>
                              </div>
                              <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:text-right">
                                <p className="text-lg font-bold">{orderTotal.toFixed(2)} DH</p>
                                <Badge
                                  className={
                                    order.status === 'ready'
                                      ? 'bg-green-500'
                                      : order.status === 'in_progress'
                                        ? 'bg-blue-500'
                                        : order.status === 'pending'
                                          ? 'bg-yellow-500'
                                          : order.status === 'completed' ||
                                              (order.status as string) === 'checked'
                                            ? 'bg-gray-500'
                                            : 'bg-red-500'
                                  }
                                >
                                  {t(`order.statuses.${order.status}`)}
                                </Badge>
                              </div>
                            </div>

                            {/* Order Items */}
                            {orderItems.length > 0 && (
                              <div className="mb-4 space-y-1.5">
                                {orderItems.map((item) => (
                                  <div key={item.id} className="flex justify-between text-xs sm:text-sm gap-2">
                                    <span className="flex-1 min-w-0 truncate">
                                      {i18n.language === 'ar'
                                        ? item.menuItem?.nameAr
                                        : item.menuItem?.nameFr}{' '}
                                      × {item.quantity}
                                    </span>
                                    <span className="font-medium whitespace-nowrap">
                                      {(
                                        (item.priceAtOrder || item.menuItem?.price || 0) *
                                        item.quantity
                                      ).toFixed(2)}{' '}
                                      DH
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t">
                              <Button
                                size="sm"
                                variant="destructive"
                                className="flex-1 w-full"
                                onClick={() => handleConfirmCancellation(order.id)}
                                disabled={confirmCancellation.isPending}
                              >
                                <CheckCircle className="w-4 h-4 me-2" />
                                <span className="text-xs sm:text-sm">{t('order.approveCancellation')}</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 w-full"
                                onClick={() => handleRejectCancellation(order.id)}
                                disabled={rejectCancellation.isPending}
                              >
                                <X className="w-4 h-4 me-2" />
                                <span className="text-xs sm:text-sm">{t('order.rejectCancellation')}</span>
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Cancellation Statistics */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-4">{t('order.cancellationStatistics')}</h2>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {t('order.cancellationsToday')}
                  </CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{cancellationStats.todayCount}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {t('order.cancellationsThisWeek')}
                  </CardTitle>
                  <TrendingDown className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{cancellationStats.weekCount}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {t('order.cancellationsThisMonth')}
                  </CardTitle>
                  <TrendingDown className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{cancellationStats.monthCount}</div>
                </CardContent>
              </Card>
            </div>

            {/* Last 5 Cancellations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  {t('order.last5Cancellations')}
                </CardTitle>
                <CardDescription>{t('order.recentCancellations')}</CardDescription>
              </CardHeader>
              <CardContent>
                {last5Cancellations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('order.noCancellations')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {last5Cancellations.map((order) => {
                      const isSubOrder = !!order.parentOrderId;
                      // For sub-orders, get table from parent order
                      let tableNumber: number | string = '?';
                      if (isSubOrder) {
                        const parentOrder = allOrders.find((o) =>
                          o.subOrders?.some((so) => so.id === order.id)
                        );
                        tableNumber = parentOrder?.table?.tableNumber || '?';
                      } else {
                        tableNumber = order.table?.tableNumber || '?';
                      }
                      const cancelledDate = order.cancellationRequestedAt
                        ? new Date(order.cancellationRequestedAt)
                        : new Date(order.updatedAt);

                      // For parent orders, get items from all sub-orders
                      // For sub-orders, use the order's own items
                      const orderItems = isSubOrder
                        ? order.items || []
                        : order.subOrders?.flatMap((subOrder) => subOrder.items || []) || [];

                      const orderTotal = orderItems.reduce((sum, item) => {
                        const price = item.priceAtOrder || item.menuItem?.price || 0;
                        return sum + price * item.quantity;
                      }, 0);

                      return (
                        <Card key={order.id} className="border-red-200 bg-red-50/50">
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <Badge className="bg-red-500">
                                    {isSubOrder ? t('order.subOrder') : t('order.order')}
                                  </Badge>
                                  <span className="font-mono text-xs sm:text-sm text-muted-foreground">
                                    #{order.id.slice(0, 8)}
                                  </span>
                                </div>
                                <p className="text-sm font-medium">
                                  {t('order.table')}: {tableNumber}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t('order.cancelledAt')}: {cancelledDate.toLocaleString()}
                                </p>
                                {order.cancellationConfirmedByName && (
                                  <p className="text-xs text-muted-foreground">
                                    {t('order.confirmedBy')}: {order.cancellationConfirmedByName}
                                  </p>
                                )}
                              </div>
                              <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:text-right">
                                <p className="text-lg font-bold">{orderTotal.toFixed(2)} DH</p>
                                <Badge className="bg-red-500">
                                  {t('order.statuses.cancelled')}
                                </Badge>
                              </div>
                            </div>

                            {/* Order Items */}
                            {orderItems.length > 0 && (
                              <div className="space-y-1.5">
                                {orderItems.map((item) => (
                                  <div key={item.id} className="flex justify-between text-xs sm:text-sm gap-2">
                                    <span className="flex-1 min-w-0 truncate">
                                      {i18n.language === 'ar'
                                        ? item.menuItem?.nameAr
                                        : item.menuItem?.nameFr}{' '}
                                      × {item.quantity}
                                    </span>
                                    <span className="font-medium whitespace-nowrap">
                                      {(
                                        (item.priceAtOrder || item.menuItem?.price || 0) *
                                        item.quantity
                                      ).toFixed(2)}{' '}
                                      DH
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
}

export function AdminCancellationDashboard() {
  return <AdminCancellationDashboardContent />;
}
